import { Box, Grid, Typography, useTheme } from "@mui/material";
import React, { useEffect } from "react";
import constants from "../../utils/constants";
import {
  CommonButton,
  CommonFileUpload,
  CommonInput,
  CommonSelect,
} from "../common/CommonElements";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import useForm from "../../hooks/useForm";
import { useDispatch, useSelector } from "react-redux";
import { register, updateUser } from "../../redux/authSlice";
import { countryRegion } from "../../utils/countryRegion";
import { registerSchema } from "../../ValidationSchema/AuthValidations/register";
import { useNavigate } from "react-router-dom";
import { getUserInfo } from "../../services/authApis";
import { useNotification } from "../../context/NotificationContext";
import { getStates, getCityId } from "../../services/authApis";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const Register = () => {
  // !--------------------------- States ---------------------------
  const [regions, setRegions] = React.useState([]);
  const [formErrors, setFormErrors] = React.useState({});
  const dispatch = useDispatch();
  const { error, loading, token } = useSelector((state) => state.auth);
const addNotification = useNotification();
  // !--------------------------- Initialize form feilds ---------------------------
  const { values, handleChange, setValues, imagePreviews, setImagePreviews } =
    useForm({
      first_name: "",
      last_name: "",
      email: "",
      telephone: "",
      fax: "",
      business_license_copy: null,
      federal_tax_id_copy: null,
      id_driver_license: null,
      // license_info:"",
      business_name: "",
      address_1: "",
      address_2: "",
      city: "",
      country: "",
      region_state: "",
      password: "",
      password_confirmation: "",
      subscribe_newsletter: false,
      remember: false,
      post_code: "",
    });

  const safeCountryRegion = countryRegion || {};
  const options = Object.keys(safeCountryRegion).map((country) => ({
    value: country,
    label: country,
  }));
  const theme = useTheme();
  const navigate = useNavigate();
  // !--------------------------- Functions---------------------------
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});
  
    let filteredValues = { ...values };
    if (token) {
      delete filteredValues.password;
      delete filteredValues.password_confirmation;
      delete filteredValues.remember;
    }

    // console.log('filteredValues', filteredValues);
    const result = registerSchema.safeParse(filteredValues);
  
    if (!result?.success) {
      const errors = {};
      result?.error?.errors?.forEach((err) => {
        errors[err?.path[0]] = err?.message;
      });
      // console.log("errors", errors);
      setFormErrors(errors);
      return;
    } else {
      // console.log('filteredValues', filteredValues);
      // console.log("token", token);
      const result = token ? await dispatch(updateUser(filteredValues)) : await dispatch(register(values));
  
      if (register.fulfilled.match(result) || updateUser.fulfilled.match(result)) {
        if (result?.payload?.code === "SUCCESS") {
          if (token) {
            addNotification("success", "Profile updated successfully.");
          } else {
            addNotification("success", "Registered successfully.");
            navigate("/my/account");
          }
        }else {
          let errors = {};
          if (result?.payload?.errors) {
            const responseErrors = result?.payload?.errors;
            for (const field in responseErrors) {
              errors[field] = responseErrors[field][0];
            }
            // console.log('API validation errors', errors);
            setFormErrors(errors);
          } else {
            setFormErrors({ general: result?.payload?.message });
          }
        }
      } else {
        addNotification('error', token ? 'Update failed. Please try again later.' : 'Registration failed. Please try again later.');
        setFormErrors({ general: token ? 'Update failed. Please try again later.' : 'Registration failed. Please try again later.' });
      }
    }
  };

  // !--------------------------- UseEffect ---------------------------
  
  useEffect(() => {
    if (values.country && countryRegion[values.country]) {
      const regionOptions = countryRegion[values.country].map((region) => ({
        value: region.name,
        label: region.name,
      }));
      setRegions(regionOptions);
    } else {
      setRegions([]);
    }
  }, [values.country, countryRegion]);
  React.useEffect(() => {
    const loadUserData = async () => {
      const userData = await getUserInfo();
      setValues({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        email: userData.email || "",
        telephone: userData.telephone || "",
        fax: userData.fax || "",
        // Keep the file paths for existing files
        business_license_copy: userData.business_license_copy || null,
        federal_tax_id_copy: userData.federal_tax_id_copy || null,
        id_driver_license: userData.id_driver_license || null,
        business_name: userData.business_name || "",
        address_1: userData.address_1 || "",
        address_2: userData.address_2 || "",
        city: userData.city || "",
        country: userData.country || "",
        region_state: userData.region_state || "",
        post_code: userData.post_code || "",
        subscribe_newsletter: userData.subscribe_newsletter ? true : false,
      });
      setImagePreviews({
        business_license_copy: userData?.business_license_copy
          ? `${process.env.REACT_APP_BASE_URL}/${userData?.business_license_copy}`
          : null,
        federal_tax_id_copy: userData?.federal_tax_id_copy
          ? `${process.env.REACT_APP_BASE_URL}/${userData?.federal_tax_id_copy}`
          : null,
        id_driver_license: userData?.id_driver_license
          ? `${process.env.REACT_APP_BASE_URL}/${userData?.id_driver_license}`
          : null,
      });
    };
  
    if (token) {
      loadUserData();
    }
  }, [token]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        mt: 2,
        overflow: "hidden",
      }}
    >
      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} sm={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              padding: 2,
              boxSizing: "border-box",
              overflow: "hidden",
              gap: 1,
              flex: 1,
            }}
          >
            <Typography
              variant="h4"
              sx={{
                color: theme.palette.heading_color.main,
                fontWeight: 600,
                fontSize: { xs: 20, sm: 24 },
              }}
            >
              {token ? "My Account Information" : constants?.register?.title}
            </Typography>
            {token ? null : (
              <Typography
                variant="body1"
                sx={{
                  color: theme.palette.heading_color.main,
                  fontWeight: 500,
                  fontSize: { xs: 13, sm: 14 },
                }}
              >
                {constants?.register?.note}
              </Typography>
            )}
            <Typography
              sx={{
                fontSize: { xs: "16px", sm: "18px" },
                fontWeight: 400,
                paddingBottom: "4px",
              }}
            >
              {constants?.register?.first_section_title}
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: "1px",
                display: "flex",
                marginBottom: "8px",
                backgroundColor: theme.palette.seprator_color.main,
              }}
            />
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.first_name_label
                  }
                </Typography>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  <CommonInput
                    placeholder={
                      constants?.register?.personal_details_section
                        ?.first_name_placeholder
                    }
                    type="text"
                    value={values.first_name}
                    name="first_name"
                    onChange={handleChange}
                  />

                  {formErrors.first_name && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        textAlign: "left",
                        fontSize: { xs: 11, sm: 12 },
                        mt: 0.5,
                      }}
                    >
                      {formErrors.first_name}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.last_name_label
                  }
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  <CommonInput
                    placeholder={
                      constants?.register?.personal_details_section
                        ?.last_name_placeholder
                    }
                    type="text"
                    value={values.last_name}
                    name="last_name"
                    onChange={handleChange}
                  />
                  {formErrors.last_name && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        textAlign: "left",
                        fontSize: { xs: 11, sm: 12 },
                        mt: 0.5,
                      }}
                    >
                      {formErrors.last_name}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {constants?.register?.personal_details_section?.email_label}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  <CommonInput
                    placeholder={
                      constants?.register?.personal_details_section
                        ?.email_placeholder
                    }
                    type="email"
                    value={values.email}
                    name="email"
                    onChange={handleChange}
                  />
                  {formErrors.email && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        textAlign: "left",
                        fontSize: { xs: 11, sm: 12 },
                        mt: 0.5,
                      }}
                    >
                      {formErrors.email}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.telephone_label
                  }
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  <CommonInput
                    placeholder={
                      constants?.register?.personal_details_section
                        ?.telephone_placeholder
                    }
                    type="tel"
                    value={values.telephone}
                    name="telephone"
                    onChange={handleChange}
                  />
                  {formErrors.telephone && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        textAlign: "left",
                        fontSize: { xs: 11, sm: 12 },
                        mt: 0.5,
                      }}
                    >
                      {formErrors.telephone}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {constants?.register?.personal_details_section?.fax_label}
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                  }}
                >
                  <CommonInput
                    placeholder={
                      constants?.register?.personal_details_section
                        ?.fax_placeholder
                    }
                    type="tel"
                    value={values.fax}
                    name="fax"
                    onChange={handleChange}
                  />
                  {formErrors.fax && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "red",
                        textAlign: "left",
                        fontSize: { xs: 11, sm: 12 },
                        mt: 0.5,
                      }}
                    >
                      {formErrors.fax}
                    </Typography>
                  )}
                </Box>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.business_license_label
                  }
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                    alignItems: "center",
                    gap: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: "150px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonFileUpload
                      handleChange={handleChange}
                      text={
                        constants?.register?.personal_details_section
                          ?.business_license_placeholder
                      }
                      icon={<CloudUploadIcon />}
                      name="business_license_copy"
                      value={values.business_license_copy}
                      accept={"image/*,.pdf"}
                    />
                    {formErrors.business_license_copy && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.business_license_copy}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      overflow: "hidden",
                      borderRadius: "50%",
                      p: "4px",
                    }}
                  >
                    {values.business_license_copy?.type==="application/pdf" ? (
                      <>
                        <PictureAsPdfIcon sx={{ color: "#b71c1c", fontSize: 30 }} />
                      </>
                    ) : (
                      <>
                        <img
                          src={imagePreviews.business_license_copy}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%"
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
              

              
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.federal_tax_label
                  }
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                    alignItems: "center",
                    gap: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: "150px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonFileUpload
                      handleChange={handleChange}
                      text={
                        constants?.register?.personal_details_section
                          ?.business_license_placeholder
                      }
                      icon={<CloudUploadIcon />}
                      name="federal_tax_id_copy"
                      value={values.federal_tax_id_copy}
                      accept={"image/*,.pdf"}
                    />
                    {formErrors.federal_tax_id_copy && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.federal_tax_id_copy}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      overflow: "hidden",
                      borderRadius: "50%",
                      p: "4px",
                    }}
                  >
                    {values.federal_tax_id_copy?.type==="application/pdf" ? (
                      <>
                        <PictureAsPdfIcon sx={{ color: "#b71c1c", fontSize: 30 }} />
                      </>
                    ) : (
                      <>
                        <img
                          src={imagePreviews.federal_tax_id_copy}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%"
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {
                    constants?.register?.personal_details_section
                      ?.id_driver_label
                  }
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    width: "100%",
                    alignItems: "center",
                    gap: { xs: 0, sm: 2 },
                  }}
                >
                  <Box
                    sx={{
                      width: "150px",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonFileUpload
                      handleChange={handleChange}
                      text={
                        constants?.register?.personal_details_section
                          ?.business_license_placeholder
                      }
                      icon={<CloudUploadIcon />}
                      name="id_driver_license"
                      value={values.id_driver_license}
                      accept={"image/*,.pdf"}
                    />
                    {formErrors.id_driver_license && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.id_driver_license}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      border: "1px solid #ccc",
                      overflow: "hidden",
                      borderRadius: "50%",
                      p: "4px",
                    }}
                  >
                    {values.id_driver_license?.type==="application/pdf" ? (
                      <>
                        <PictureAsPdfIcon sx={{ color: "#b71c1c", fontSize: 30 }} />
                      </>
                    ) : (
                      <>
                        <img
                          src={imagePreviews.id_driver_license}
                          alt="Preview"
                          style={{
                            width: "100%",
                            height: "100%",
                            borderRadius: "50%"
                          }}
                        />
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              <Typography
                sx={{
                  mt: 2,
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 400,
                  paddingBottom: "4px",
                }}
              >
                {constants?.register?.address_section?.address_label}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  display: "flex",
                  marginBottom: "8px",
                  backgroundColor: theme.palette.seprator_color.main,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.business_name_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonInput
                      placeholder={
                        constants?.register?.address_section
                          ?.business_name_label_placeholder
                      }
                      type="text"
                      value={values.business_name}
                      name="business_name"
                      onChange={handleChange}
                    />
                    {formErrors.business_name && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.business_name}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.address1_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonInput
                      placeholder={
                        constants?.register?.address_section
                          ?.address1_placeholder
                      }
                      type="text"
                      value={values.address_1}
                      name="address_1"
                      onChange={handleChange}
                    />
                    {formErrors.address_1 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.address_1}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.address2_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonInput
                      placeholder={
                        constants?.register?.address_section
                          ?.address2_placeholder
                      }
                      type="text"
                      value={values.address_2}
                      name="address_2"
                      onChange={handleChange}
                    />
                    {formErrors.address_2 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.address_2}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.city_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonInput
                      placeholder={
                        constants?.register?.address_section?.city_placeholder
                      }
                      type="text"
                      value={values.city}
                      name="city"
                      onChange={handleChange}
                    />
                    {formErrors.city && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.city}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.postcode_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonInput
                      placeholder={
                        constants?.register?.address_section
                          ?.postcode_placeholder
                      }
                      type="text"
                      value={values.post_code || ""}
                      name="post_code"
                      onChange={handleChange}
                    />
                    {formErrors.post_code && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.post_code}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* country select  */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.country_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonSelect
                      placeholder={
                        constants?.register?.address_section
                          ?.country_placeholder || "Select a country"
                      }
                      value={values.country || ""}
                      name="country"
                      onChange={handleChange}
                      options={options}
                      height={"40px"}
                    />
                    {formErrors.country && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.country}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {/* related regions */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 1fr",
                    gap: 2,
                    alignItems: "center",
                    width: "100%",
                    padding: "8px 0",
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.heading_color.main,
                      fontSize: { xs: 13, sm: 14 },
                    }}
                  >
                    {constants?.register?.address_section?.state_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 0,
                    }}
                  >
                    <CommonSelect
                      placeholder={
                        constants?.register?.address_section
                          ?.state_placeholder || "Select a country"
                      }
                      value={values.region_state || ""}
                      name="region_state"
                      onChange={handleChange}
                      options={regions}
                      height={"40px"}
                      disabled={!values.country}
                    />
                    {formErrors.region_state && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "red",
                          textAlign: "left",
                          fontSize: { xs: 11, sm: 12 },
                          mt: 0.5,
                        }}
                      >
                        {formErrors.region_state}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
              {token ? null : (
                <>
                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: { xs: "16px", sm: "18px" },
                      fontWeight: 400,
                      paddingBottom: "4px",
                    }}
                  >
                    {constants?.register?.password_section?.password_label}
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      height: "1px",
                      display: "flex",
                      marginBottom: "8px",
                      backgroundColor: theme.palette.seprator_color.main,
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "150px 1fr",
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                        padding: "8px 0",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.heading_color.main,
                          fontSize: { xs: 13, sm: 14 },
                        }}
                      >
                        {constants?.register?.password_section?.password_label}
                      </Typography>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <CommonInput
                          placeholder={
                            constants?.register?.password_section
                              ?.password_placeholder
                          }
                          type="password"
                          value={values.password}
                          name="password"
                          onChange={handleChange}
                        />
                        {formErrors.password && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "red",
                              textAlign: "left",
                              fontSize: { xs: 11, sm: 12 },
                              mt: 0.5,
                            }}
                          >
                            {formErrors.password}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "150px 1fr",
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                        padding: "8px 0",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 600,
                          color: theme.palette.heading_color.main,
                          fontSize: { xs: 13, sm: 14 },
                        }}
                      >
                        {
                          constants?.register?.password_section
                            ?.confirm_password_label
                        }
                      </Typography>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                          gap: 0,
                        }}
                      >
                        <CommonInput
                          placeholder={
                            constants?.register?.password_section
                              ?.confirm_password_placeholder
                          }
                          type="password"
                          value={values.password_confirmation}
                          name="password_confirmation"
                          onChange={handleChange}
                        />
                        {formErrors.password_confirmation && (
                          <Typography
                            variant="body2"
                            sx={{
                              color: "red",
                              textAlign: "left",
                              fontSize: { xs: 11, sm: 12 },
                              mt: 0.5,
                            }}
                          >
                            {formErrors.password_confirmation}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    {/* remeber me */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "150px 1fr",
                        gap: 2,
                        alignItems: "center",
                        width: "100%",
                        padding: "8px 0",
                      }}
                    >
                      <text></text>
                      {/* check box */}
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <input
                          type="checkbox"
                          id="terms"
                          name="remember"
                          onChange={handleChange}
                          checked={values.remember}
                        />
                        <label htmlFor="terms">
                          <Typography
                            variant="h6"
                            sx={{
                              color: theme.palette.heading_color.main,
                              fontSize: { xs: 13, sm: 14 },
                            }}
                          >
                            {constants?.register?.password_section?.remember_me}
                          </Typography>
                        </label>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}

              <Typography
                sx={{
                  mt: 2,
                  fontSize: { xs: "16px", sm: "18px" },
                  fontWeight: 400,
                  paddingBottom: "4px",
                }}
              >
                {constants?.register?.news_letter_Section?.news_title}
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  height: "1px",
                  display: "flex",
                  marginBottom: "8px",
                  backgroundColor: theme.palette.seprator_color.main,
                }}
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "150px 1fr",
                  gap: 2,
                  alignItems: "center",
                  width: "100%",
                  padding: "8px 0",
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    color: theme.palette.heading_color.main,
                    fontSize: { xs: 13, sm: 14 },
                  }}
                >
                  {constants?.register?.news_letter_Section?.subscribe}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                  }}
                >
                  <input
                    type="radio"
                    id="yes"
                    name="subscribe_newsletter"
                    value="true"
                    checked={values.subscribe_newsletter === true}
                    onChange={handleChange}
                  />
                  <label htmlFor="yes">
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.heading_color.main,
                        fontSize: { xs: 13, sm: 14 },
                      }}
                    >
                      {
                        constants?.register?.news_letter_Section?.radio_labels
                          ?.yes
                      }
                    </Typography>
                  </label>
                  <input
                    type="radio"
                    id="no"
                    onChange={handleChange}
                    name="subscribe_newsletter"
                    value="false"
                    checked={values.subscribe_newsletter === false}
                  />
                  <label htmlFor="no">
                    <Typography
                      variant="h6"
                      sx={{
                        color: theme.palette.heading_color.main,
                        fontSize: { xs: 13, sm: 14 },
                      }}
                    >
                      {
                        constants?.register?.news_letter_Section?.radio_labels
                          ?.no
                      }
                    </Typography>
                  </label>
                </Box>
              </Box>
              {/* {error && <Typography color="error">{error}</Typography>} */}

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <CommonButton
                  action={handleSubmit}
                  text={token ? "Update" : constants?.register?.button_text}
                  bgColor={theme.palette.secondary.main}
                  height="40px"
                  width="150px"
                  loading={loading}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Register;
